import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { formatEnrichmentMoney } from "@/lib/enrichmentMoney";
import { Button } from "@/components/ui/button";
import {
  getEnrichmentHistory,
  type EnrichmentHistoryResponse,
} from "@/api/enrichment";

interface EnrichmentHistoryProps {
  slug: string;
  userId: string;
  startAt: string | null;
  endAt: string;
}

const FIELD_LABELS: Record<string, string> = {
  name: "Name",
  title: "Title",
  email: "Email",
  phone: "Phone",
  linkedin: "LinkedIn",
  contact_created: "Contact created",
};
const KIND_LABELS = {
  enriched: "Enrichment",
  adjustment: "Adjustment",
  correction: "Correction (unpaid)",
  not_found: "Not found (unpaid)",
};
const dateTime = new Intl.DateTimeFormat("en-GB", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "UTC",
});

/** Loaded only when expanded; amounts and authorization come from the server. */
export function EnrichmentHistory({
  slug,
  userId,
  startAt,
  endAt,
}: EnrichmentHistoryProps) {
  const [page, setPage] = useState(1);
  const [retry, setRetry] = useState(0);
  const [data, setData] = useState<EnrichmentHistoryResponse | null>(null);
  const [status, setStatus] = useState<"loading" | "ok" | "error">("loading");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    getEnrichmentHistory(slug, {
      user_id: userId,
      start_at: startAt,
      end_at: endAt,
      page,
    })
      .then((response) => {
        if (cancelled) return;
        setData(response);
        setStatus("ok");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [slug, userId, startAt, endAt, page, retry]);

  if (status === "loading") {
    return (
      <div role="status" className="flex items-center gap-2 p-4 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading prospect details…
      </div>
    );
  }
  if (status === "error") {
    return (
      <div role="alert" className="space-y-2 p-4 text-rose-700">
        <p>Couldn’t load prospect details. The backend must support enrichment history.</p>
        <Button variant="outline" size="sm" onClick={() => setRetry((n) => n + 1)}>
          Try again
        </Button>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-3 p-4">
      <p className="text-xs text-slate-500">
        Calculated using current rates ({data.currency}), not a payment record. Recorded changes take
        precedence over fields found, even when no fields changed. Corrections and not found
        pay nothing. Adjustments use their recorded rate snapshot and do not add a lead. Dates are UTC. Each line is one activity, so a company can appear more than once.
      </p>
      {data.items.length === 0 ? (
        <p className="text-sm text-slate-500">No enrichment activities in this window.</p>
      ) : (
        <table className="w-full text-left text-xs">
          <caption className="sr-only">Prospect earnings details</caption>
          <thead className="text-slate-500">
            <tr>
              <th className="px-2 py-2">Company</th>
              <th className="px-2 py-2">Date (UTC)</th>
              <th className="px-2 py-2">Fields found</th>
              <th className="px-2 py-2">Calculation fields / rates</th>
              <th className="px-2 py-2">Activity</th>
              <th className="px-2 py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item) => (
              <tr key={item.activity_id} className="border-t border-slate-900/[0.06] align-top">
                <td className="px-2 py-3 font-medium text-slate-800">
                  {item.company_name ?? (
                    item.company_id === null ? "Company unavailable" : `Company #${item.company_id}`
                  )}
                  <span className="block font-normal text-slate-400">Activity #{item.activity_id}</span>
                </td>
                <td className="whitespace-nowrap px-2 py-3">
                  {dateTime.format(new Date(item.created_at))}
                </td>
                <td className="px-2 py-3">
                  {item.fields_found === null ? "Not recorded" : (
                    item.fields_found.map((field) => FIELD_LABELS[field] ?? field).join(", ") || "None"
                  )}
                </td>
                <td className="px-2 py-3">
                  {item.fields_counted.map((field) => {
                    const rate = (item.rate_basis === "snapshot" ? item.rate_snapshot : data.rates)?.[field];
                    return `${FIELD_LABELS[field] ?? field} (${rate === undefined ? "no rate" : formatEnrichmentMoney(rate, data.currency, 6)})`;
                  }).join(", ") || "None"}
                </td>
                <td className="px-2 py-3">
                  {KIND_LABELS[item.kind]}
                  {item.original_activity_id != null && <span className="block text-xs text-slate-500">For activity #{item.original_activity_id} · Rate snapshot</span>}
                  {item.reason && <span className="block text-xs text-slate-500">{item.reason}</span>}
                </td>
                <td className="px-2 py-3 text-right tabular-nums">{formatEnrichmentMoney(item.amount, data.currency, 6)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
        <span>{data.total} activities · Page {data.page} of {Math.max(1, Math.ceil(data.total / data.page_size))}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((n) => n - 1)}>Previous</Button>
          <Button size="sm" variant="outline" disabled={data.page * data.page_size >= data.total} onClick={() => setPage((n) => n + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}
